import React, { Fragment, useEffect, useRef, useState } from "react";

import PropTypes from "prop-types";

import { ADDRESS, ADDRESS_FIELDS } from "constants/address";
import { COUNTRY_CODES, MARKET_CENTER } from "constants/market";
import { SERVICE_METHOD, SERVICE_METHOD_SORT } from "constants/order";

import RequiredMark from "components/RequiredMark";
import "components/StoreLocator/Market/PA.css";
import GoogleMapsTypeAhead from "components/StoreLocator/Strategy/GoogleMapsTypeAhead";
import Input from "components/StoreLocator/Strategy/Input";
import PinDrop, { MAP_OPTIONS } from "components/StoreLocator/Strategy/PinDrop";
import Select from "components/StoreLocator/Strategy/Select";

const MARKET_CODE = COUNTRY_CODES.PANAMA;
const placesMethods = Object.freeze([
  SERVICE_METHOD.CARRYOUT,
  SERVICE_METHOD.DELIVERY,
]);

const ADDRESS_TYPES = Object.freeze({
  [ADDRESS.HOUSE]: {
    fields: [
      ADDRESS_FIELDS[ADDRESS.NEIGHBORHOOD],
      ADDRESS_FIELDS[ADDRESS.STREET_NAME],
      ADDRESS_FIELDS[ADDRESS.UNIT_NUMBER],
    ],
    key: ADDRESS.HOUSE,
  },
  [ADDRESS.APARTMENT]: {
    fields: [
      ADDRESS_FIELDS[ADDRESS.NEIGHBORHOOD],
      ADDRESS_FIELDS[ADDRESS.STREET_NAME],
      ADDRESS_FIELDS[ADDRESS.LOCATION_NAME],
      ADDRESS_FIELDS[ADDRESS.STREET_NUMBER],
      ADDRESS_FIELDS[ADDRESS.APARTMENT_NUMBER],
    ],
    key: ADDRESS.APARTMENT,
  },
  [ADDRESS.OTHER]: {
    fields: [
      ADDRESS_FIELDS[ADDRESS.NEIGHBORHOOD],
      ADDRESS_FIELDS[ADDRESS.STREET_NAME],
      ADDRESS_FIELDS[ADDRESS.PLACE_NAME_2],
      ADDRESS_FIELDS[ADDRESS.STREET_NUMBER],
      ADDRESS_FIELDS[ADDRESS.ROOM_NUMBER],
    ],
    key: ADDRESS.OTHER,
  },
});
const MARKET_ZOOM = 10;

const optionalFields = [ADDRESS.UNIT_NUMBER];

const getAddressFields = (addressType) => {
  return ADDRESS_TYPES[addressType]
    ? ADDRESS_TYPES[addressType].fields
    : ADDRESS_TYPES[ADDRESS.HOUSE].fields;
};

const PA = (props) => {
  const {
    address,
    addressLine2,
    addressType,
    getRegions,
    handleChange,
    locationName,
    maps,
    neighborhood,
    regions,
    regionCode,
    serviceMethod,
    streetName,
    streetNumber,
    t,
    unitNumber,
    setUsesPlaces,
  } = props;

  const [invalidFields, setInvalidFields] = useState([]);

  const map = useRef();
  const geocoderService = new maps.Geocoder();
  const geocoderOK = maps.GeocoderStatus.OK;

  useEffect(() => {
    getRegions(MARKET_CODE);
    placesMethods.includes(serviceMethod) && setUsesPlaces(true);
  }, [setUsesPlaces, serviceMethod]);

  const fieldClasses = "grid__cell--1 grid__cell--1/2@desktop";

  const getGeocodedResult = (request, callback) => {
    geocoderService.geocode(request, (response, status) => {
      if (status !== geocoderOK) {
        props.onError(status);
        return;
      }

      const { address_components, geometry, place_id } =
        response.find((address) => address.types.includes("route")) ||
        response[0];

      callback({
        addressComponents: address_components,
        geometry,
        placeId: place_id,
      });
    });
  };

  const getFormattedAddress = ({
    address,
    addressLine2,
    neighborhood,
    locationName,
    regionCode,
    streetName,
    streetNumber,
  }) => {
    const unnumberedAddress = [
      addressLine2,
      neighborhood,
      locationName,
      regionCode,
      streetName,
    ].reduce((formattedAddress, field) => {
      if (!field) return formattedAddress;
      return formattedAddress ? `${formattedAddress}, ${field}` : field;
    }, "");

    return streetNumber
      ? `${streetNumber} ${address || unnumberedAddress}`
      : address || unnumberedAddress;
  };

  const handlePinDrop = (latLng) => {
    getGeocodedResult({ location: latLng }, ({ placeId, geometry }) => {
      const updateObject = Object.assign(
        {},
        ...Object.keys(ADDRESS_FIELDS)
          .filter((field) => field !== ADDRESS.ADDRESS_TYPE)
          .map((field) => ({ [field]: "" })),
        {
          latitude: geometry.location.lat(),
          longitude: geometry.location.lng(),
        },
        {
          placeId,
        },
        {
          address,
          addressLine2,
          neighborhood,
          streetName,
          unitNumber,
        }
      );
      handleChange(updateObject);
    });
  };

  const validateInputs = () => {
    const propKeys = Object.keys(props);

    setInvalidFields([
      ...Object.keys(ADDRESS_FIELDS).filter(
        (field) =>
          propKeys.includes(field) &&
          props[field]?.length === 0 &&
          !optionalFields.includes(field)
      ),
    ]);
  };

  const handleFocus = (update) => {
    const [fieldName] = Object.keys(update);

    setInvalidFields([
      ...invalidFields.filter(
        (invalidFieldName) => fieldName !== invalidFieldName
      ),
    ]);
  };

  const handleSearch = () => {
    const formattedAddress = getFormattedAddress({
      address,
      addressLine2,
      locationName,
      neighborhood,
      regionCode,
      streetName,
      streetNumber,
    });

    getGeocodedResult({ address: formattedAddress }, ({ geometry }) => {
      map.current.dropPin(geometry.location);
    });
  };

  const handleClear = () => {
    const addressFields = getAddressFields(addressType);
    const updates = addressFields.reduce(
      (updatesObject, { mapping: fieldName }) => {
        updatesObject[fieldName] = "";
        return updatesObject;
      },
      {
        address: "",
        city: "",
        neighborhood: "",
        streetName: "",
        streetNumber: "",
        addressLine2: "",
      }
    );

    handleChange(updates);
  };

  const countryCodeList = window.ENVCONFIG.includeUSMarketForTestStore
    ? [MARKET_CODE, COUNTRY_CODES.UNITED_STATES]
    : [MARKET_CODE];

  switch (serviceMethod) {
    case SERVICE_METHOD.DELIVERY:
      return (
        <div className="grid">
          <div className="grid__cell--1 grid__cell--1/2@desktop margin__bottom--1_25rem">
            <PinDrop
              mapOptions={Object.assign({}, MAP_OPTIONS, {
                mapTypeControl: true,
              })}
              mapCenter={MARKET_CENTER.PANAMA}
              mapZoom={MARKET_ZOOM}
              maps={maps}
              onChange={handlePinDrop}
              ref={map}
            />
          </div>

          <div className="grid__cell--1 grid__cell--1/2@desktop">
            <Fragment>
              <GoogleMapsTypeAhead
                className="grid__cell--1"
                input={address}
                label={t(ADDRESS_FIELDS[ADDRESS.ADDRESS].label)}
                maps={maps}
                name="address"
                onChange={({ address: addressValue, placeId }) =>
                  handleChange({
                    address: addressValue,
                    placeId,
                  })
                }
                options={{
                  componentRestrictions: {
                    country: countryCodeList,
                  },
                  types: ["geocode"],
                }}
                quidBase="store-locator-address"
                required
              />
            </Fragment>

            <Select
              className="grid__cell--1"
              label={t("customer:locations.address.address_type")}
              handleChange={(addressType) => {
                handleChange({ addressType, address });
              }}
              name="addressType"
              quidBase="store-locator-address-type"
              source={Object.keys(ADDRESS_TYPES).reduce((types, type) => {
                const { key, label } = ADDRESS_FIELDS[type];
                types[key] = t(label);
                return types;
              }, {})}
              value={addressType}
            />

            <Select
              className="grid__cell--1"
              label={t("customer:locations.address.address_line_2")}
              handleChange={(addressLine2) => {
                handleChange({ addressLine2 });
              }}
              name="Address_Line_2"
              quidBase="store-locator-address-line-2"
              source={Object.freeze({
                selectAddressLine2: t("customer:locations.address.select"),
                ...regions,
              })}
              value={addressLine2}
              required={true}
            />

            {getAddressFields(addressType).map(({ label, mapping: key }) => {
              return (
                <Input
                  className={fieldClasses}
                  handleBlur={validateInputs}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  isInvalid={
                    invalidFields.includes(key) && props[key].length === 0
                  }
                  isRequired={!optionalFields.includes(key)}
                  key={key}
                  label={t(label)}
                  name={key}
                  quidBase="store-locator-address-field"
                  t={t}
                  value={props[key]}
                />
              );
            })}
          </div>

          <p className="grid__cell--1">
            <RequiredMark /> {t("shared:forms.indicates_required")}
          </p>
          <div className="grid__cell--1">
            <div className="grid form-controls">
              <button
                className="btn grid__cell--1/2 grid__cell--1/4@widescreen"
                data-icon="clear"
                data-quid="store-locator-clear"
                onClick={handleClear}
                type="button"
              >
                {t("shared:action.clear_form")}
              </button>
              <button
                className="btn grid__cell--1/2 grid__cell--1/4@widescreen"
                data-icon="pin_drop"
                data-quid="store-locator-search"
                onClick={handleSearch}
                type="button"
              >
                {t("shared:action.drop_pin")}
              </button>
            </div>
          </div>
        </div>
      );
    case SERVICE_METHOD.CARRYOUT:
    default:
      return (
        <GoogleMapsTypeAhead
          className="grid__cell--1"
          input={address}
          label={t(ADDRESS_FIELDS[ADDRESS.ADDRESS].label)}
          maps={maps}
          name="address"
          onChange={({ address: addressValue, placeId }) =>
            handleChange({
              address: addressValue,
              placeId,
            })
          }
          options={{
            componentRestrictions: {
              country: countryCodeList,
            },
            types: ["geocode"],
          }}
          quidBase="store-locator-address"
        />
      );
  }
};

PA.propTypes = {
  address: PropTypes.string,
  addressType: PropTypes.string,
  getRegions: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  addressLine2: PropTypes.string,
  locationName: PropTypes.string,
  maps: PropTypes.objectOf(PropTypes.any).isRequired,
  neighborhood: PropTypes.string,
  onError: PropTypes.func,
  regions: PropTypes.object,
  regionCode: PropTypes.string,
  serviceMethod: PropTypes.oneOf(SERVICE_METHOD_SORT),
  streetName: PropTypes.string,
  streetNumber: PropTypes.string,
  setUsesPlaces: PropTypes.func.isRequired,
  unitNumber: PropTypes.string,
};

PA.defaultProps = {
  address: "",
  addressType: ADDRESS.LANDED,
  onError: (error) => {
    //eslint-disable-next-line no-console
    window.ENVCONFIG.logErrors && console.error(error);
  },
  serviceMethod: SERVICE_METHOD.CARRYOUT,
};

export default PA;
